import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { XStack, YStack, Card } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DragDropWrapper from '@components/Global/DragDropWrapper';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import apis from '@api/api';

interface ModernTeamManagementProps {
  project: any;
  onAddTeamMember: () => void;
  onTeamUpdated?: () => void; // Callback to refresh project data
}

const ModernTeamManagement: React.FC<ModernTeamManagementProps> = ({
  project,
  onAddTeamMember,
  onTeamUpdated
}) => {
  const theme = themeSelector();
  const [isAddingMember, setIsAddingMember] = useState(false);

  // Create enhanced team list that includes project owner
  const enhancedTeam = useMemo(() => {
    if (!project) return [];

    const existingTeam = project.team || [];
    const ownerId = project.owner;

    // Check if owner is already in the team
    const ownerInTeam = existingTeam.some(member => member._id === ownerId);

    if (ownerInTeam || !ownerId) {
      return existingTeam;
    }

    // Add owner to the team list if not already present
    // TODO: Fetch owner details from user ID - for now using mock data
    const ownerMember = {
      _id: ownerId,
      name: 'Project Owner',
      email: 'owner@example.com',
      role: 'Owner',
      isOwner: true,
      profile_information: {
        name: 'Project Owner',
        surname: '',
        avatar: { img: null }
      }
    };

    return [ownerMember, ...existingTeam];
  }, [project]);

  // Handle adding team member via drag and drop
  const handleAddTeamMember = async (userData: any) => {
    if (!userData || !project?._id) {
      throw new Error('Missing user or project data');
    }

    setIsAddingMember(true);

    try {
      // Check if user is already in the team
      const isAlreadyMember = enhancedTeam.some((member: any) => member._id === userData?._id);

      if (isAlreadyMember) {
        throw new Error(`${userData?.profile_information?.name || 'This user'} is already a member of the project team.`);
      }

      // Prepare the team member data in the format expected by the backend
      const teamMember = {
        _id: userData?._id,
        name: userData?.profile_information?.name || 'Unknown User',
        email: userData?.email || userData?.profile_information?.email || 'No email',
        role: 'Team Member', // Default role for dragged users
        profile_information: userData?.profile_information || {}
      };

      console.log('🔄 Adding team member:', teamMember);

      // Call the API to add the team member
      await apis.addTeamMember(project._id, [teamMember]);

      // Refresh the project data to show the new team member
      if (onTeamUpdated) {
        onTeamUpdated();
      }

    } catch (error) {
      console.error('Failed to add team member:', error);
      throw error; // Re-throw to let DragDropWrapper handle the error dialog
    } finally {
      setIsAddingMember(false);
    }
  };


  return (
    <ScrollView style={{ 
      flex: 1, 
      backgroundColor: theme ? '#f9fafb' : '#111827',
      padding: 20 
    }}>
      <Text style={{
        fontSize: 20,
        fontWeight: '700',
        color: theme ? '#111827' : '#ffffff',
        marginBottom: 16,
      }}>
        Team Management
      </Text>

      <DragDropWrapper
        onConfirmDrop={handleAddTeamMember}
        confirmDialogTitle="Add Team Member"
        confirmDialogMessage={(userData) => `Add ${userData?.profile_information?.name || 'this user'} to the project team?`}
        dropZoneMessage={(userData) => `Drop ${userData?.profile_information?.name || 'user'} here to add to team`}
        dropZoneIcon="person-add"
        disabled={isAddingMember}
      >
        <Card
          padding="$4"
          backgroundColor={theme ? '#ffffff' : '#1f2937'}
          borderRadius="$3"
        >
          <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: theme ? '#111827' : '#ffffff',
            }}>
              Team Members ({enhancedTeam.length})
            </Text>

            <TouchableOpacity
              onPress={onAddTeamMember}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 8,
                backgroundColor: '#3b82f6',
                borderRadius: 8,
              }}
            >
              <MaterialIcons name="person-add" size={16} color="#ffffff" />
              <Text style={{ color: '#ffffff', fontSize: 14, fontWeight: '500' }}>
                Add Member
              </Text>
            </TouchableOpacity>
          </XStack>

          {enhancedTeam.length > 0 ? (
            enhancedTeam.map((member, index) => {
              // Get name and surname from profile_information, fallback to direct fields
              const firstName = member.profile_information?.name || member.name || '';
              const surname = member.profile_information?.surname || member.surname || '';
              const fullName = firstName && surname 
                ? `${firstName} ${surname}` 
                : firstName || member.name || 'Unknown User';
              
              // Get avatar - can be in profile_information.avatar.img or member.avatar
              const avatar = member.profile_information?.avatar?.img || member.profile_information?.avatar || member.avatar;
              
              // Get email
              const email = member.email || member.profile_information?.email || 'No email';
              
              return (
              <XStack
                key={member._id || index}
                alignItems="center"
                gap="$3"
                paddingVertical="$3"
                borderBottomWidth={index < enhancedTeam.length - 1 ? 1 : 0}
                borderBottomColor={theme ? '#e5e7eb' : '#374151'}
              >
                <View style={{ width: 48, height: 48, borderRadius: 24, overflow: 'hidden' }}>
                  <AvatarHandler
                    avatar={avatar}
                    avatarImageStyle={{
                      width: 48,
                      height: 48,
                      borderRadius: 24,
                    }}
                    replacementImageStyle={{
                      width: 48,
                      height: 48,
                      borderRadius: 24,
                    }}
                    withShadow={false}
                  />
                </View>
                <YStack flex={1}>
                  <XStack alignItems="center" gap="$2">
                    <Text style={{
                      fontSize: 16,
                      fontWeight: '600',
                      color: theme ? '#111827' : '#ffffff',
                    }}>
                      {fullName}
                    </Text>
                    {member.isOwner && (
                      <MaterialIcons
                        name="star"
                        size={16}
                        color="#f59e0b"
                      />
                    )}
                  </XStack>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#6b7280' : '#9ca3af',
                  }}>
                    {email}
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: member.isOwner ? '#f59e0b' : (theme ? '#6b7280' : '#9ca3af'),
                    fontWeight: member.isOwner ? '600' : '400',
                  }}>
                    {member.role || 'Team Member'}
                  </Text>
                </YStack>

                <TouchableOpacity
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: theme ? '#f3f4f6' : '#374151',
                  }}
                >
                  <MaterialIcons
                    name="more-vert"
                    size={20}
                    color={theme ? '#111827' : '#ffffff'}
                  />
                </TouchableOpacity>
              </XStack>
            )})
          ) : (
            <View style={{
              alignItems: 'center',
              paddingVertical: 40,
            }}>
              <MaterialIcons
                name="group"
                size={48}
                color={theme ? '#9ca3af' : '#6b7280'}
              />
              <Text style={{
                marginTop: 16,
                fontSize: 16,
                color: theme ? '#6b7280' : '#9ca3af',
                textAlign: 'center',
              }}>
                No team members yet
              </Text>
              <TouchableOpacity
                onPress={onAddTeamMember}
                style={{
                  marginTop: 16,
                  paddingHorizontal: 20,
                  paddingVertical: 10,
                  backgroundColor: '#3b82f6',
                  borderRadius: 8,
                }}
              >
                <Text style={{ color: '#ffffff', fontWeight: '500' }}>
                  Add First Member
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </Card>
      </DragDropWrapper>
    </ScrollView>
  );
};

export default ModernTeamManagement;